'use client';

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Popover,
  TextField,
  InputAdornment,
  Typography,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { searchIcons, getIconComponent } from './iconRegistry';

export interface IconSelectorProps {
  value?: string;
  onChange: (value: string) => void;
  size?: 'small' | 'medium';
  placeholder?: string;
}

const ICON_GRID_COLS = 7;
const ICON_CELL_SIZE = 32;
const POPOVER_WIDTH = 264;
const POPOVER_HEIGHT = 340;

/** 아이콘 미선택 상태를 표현하는 점선 원 */
function EmptyCircle({ size }: { size: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: '1.5px dashed',
        borderColor: 'text.disabled',
      }}
    />
  );
}

/**
 * MUI 아이콘 선택기 - 버튼 클릭 시 팝오버 표시
 * 빈 상태는 점선 원으로 표현, 팝오버 첫 셀에서 선택 해제 가능
 */
export function IconSelector({
  value,
  onChange,
  size = 'small',
  placeholder = '아이콘',
}: IconSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');

  const iconSize = size === 'small' ? 20 : 24;
  const buttonSize = iconSize + 16;

  const results = useMemo(() => searchIcons(search), [search]);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch('');
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearch('');
  }, []);

  const handleSelect = useCallback(
    (name: string) => {
      onChange(name);
      handleClose();
    },
    [onChange, handleClose]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange('');
    },
    [onChange]
  );

  const SelectedIcon = value ? getIconComponent(value) : null;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={value || placeholder} placement="top" enterDelay={400}>
        <IconButton
          size={size}
          onClick={handleOpen}
          sx={{
            width: buttonSize,
            height: buttonSize,
            border: '1px solid',
            borderColor: value ? 'divider' : 'divider',
            bgcolor: 'action.hover',
            '&:hover': { bgcolor: 'action.selected' },
            '& svg': { fontSize: iconSize },
          }}
          aria-label={placeholder}
        >
          {SelectedIcon ? <SelectedIcon /> : <EmptyCircle size={iconSize - 4} />}
        </IconButton>
      </Tooltip>

      {/* 선택 해제 버튼 */}
      {value && (
        <Tooltip title="선택 안함" placement="top">
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{
              width: 18,
              height: 18,
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'error.lighter', color: 'error.main' },
              '& svg': { fontSize: 12 },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              width: POPOVER_WIDTH,
              maxHeight: POPOVER_HEIGHT,
              mt: 0.5,
              overflow: 'hidden',
              zIndex: 1400,
            },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <TextField
            size="small"
            placeholder="아이콘 검색..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiInputBase-root': { bgcolor: 'action.hover' } }}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${ICON_GRID_COLS}, ${ICON_CELL_SIZE}px)`,
              gap: 0.5,
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 200,
              maxHeight: 260,
            }}
          >
            {/* 선택 안함 셀 */}
            <Tooltip title="선택 안함" placement="top" enterDelay={300}>
              <IconButton
                size="small"
                onClick={() => {
                  onChange('');
                  handleClose();
                }}
                sx={{
                  width: ICON_CELL_SIZE,
                  height: ICON_CELL_SIZE,
                  minWidth: ICON_CELL_SIZE,
                  minHeight: ICON_CELL_SIZE,
                  boxSizing: 'border-box',
                  border: '1px dashed',
                  borderColor: !value ? 'primary.main' : 'divider',
                  bgcolor: !value ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover', borderColor: 'error.main' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloseIcon
                  sx={{ fontSize: 14, color: !value ? 'primary.main' : 'text.disabled' }}
                />
              </IconButton>
            </Tooltip>

            {results.map(({ name, component: Icon }) => (
              <Tooltip key={name} title={name} placement="top" enterDelay={300}>
                <IconButton
                  size="small"
                  onClick={() => handleSelect(name)}
                  sx={{
                    width: ICON_CELL_SIZE,
                    height: ICON_CELL_SIZE,
                    minWidth: ICON_CELL_SIZE,
                    minHeight: ICON_CELL_SIZE,
                    boxSizing: 'border-box',
                    border: '1px solid transparent',
                    ...(value === name && {
                      boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`,
                    }),
                    bgcolor: value === name ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    '& svg': { fontSize: 20 },
                  }}
                >
                  <Icon />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          <Typography variant="caption" color="text.disabled" textAlign="right">
            {results.length}개
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
}
